import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { AUTH_TOKEN } from 'constants/AuthConstant';
import { message } from 'antd';

export const socketEvents = {
	adminNotifications: 'admin_notifications',
	markNotificationsRead: 'admin_notifications_marked_read',
	newAdminNotification: 'new_admin_notification',
	error: 'error',
	getNotifications: 'get_admin_notifications',
	clearNotifications: 'mark_all_admin_notifications_read',
};

const SocketContext = createContext({
	socket: null,
	isConnected: false,
	socketEvents: socketEvents,
	initiateSocket: null,
	notifications: [],
	fetchNotifications: null,
	clearNotifications: null,
});

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
	const user = 'Tarik Djemil';
	const token = localStorage.getItem(AUTH_TOKEN);
	const socket = useRef(null);
	const retryCountRef = useRef(0);
	const [connected, setConnected] = useState(false);
	const [notifications, setNotifications] = useState([]);

	const initiateSocket = (onConnect) => {
		if (!user || socket.current?.connected) return;

		socket.current = io('wss://api.loffre.ma/', {
			extraHeaders: {
				authorization: `Bearer ${token}`,
			},
		});

		socket.current.on('connect', () => {
			setConnected(true);
			retryCountRef.current = 0;

			// 👇 Immediately request notifications on connect
			// emit directly, no need for fetchNotifications()
			socket.current.emit(socketEvents.getNotifications);
			if (onConnect) setTimeout(onConnect, 500);
		});

		socket.current.on('connect_error', (err) => {
			console.error('Connection error:', err.message);
		});
 

		// reconnect
		socket.current.on('disconnect', (reason) => {
			console.warn('⚠️ Disconnected:', reason);
			setConnected(false);

			if (retryCountRef.current < 3) {
				retryCountRef.current += 1;

				setTimeout(() => {
					if (!socket.current?.connected) {
						initiateSocket();
					}
				}, 2000);
			} else {
				console.warn('⛔ Max retry attempts reached');
			}
		});


		// 👇 Listen to backend events
		socket.current.on('admin_notifications_response', (data) => {
			setNotifications((prev) => {
				if (data?.data.page === 1) {
					return data;
				}

				return {
					...data,
					data: {
						...data.data,
						results: [...prev.data.results, ...data.data.results],
					},
				};
			});
		});

		socket.current.on('new_admin_notification', (data) => {
			message.info(data.data?.message || 'You have a new notification');
			socket.current.emit(socketEvents.getNotifications);
		});

		socket.current.on('admin_notifications_marked_read', (data) => {
			setNotifications([]); // clear locally when server confirms
		});

		socket.current.on('error', (data) => {
			console.error('🚨 Socket error:', data);
		});

		socket.current.on(socketEvents.error, (data) => {
			console.error('🚨 Socket error:', data);
		});
	};

	// ✅ Function to request notifications
	const fetchNotifications = (pageNumber = 1) => {
		if (socket.current && connected) {
			socket.current.emit(socketEvents.getNotifications, {
				page: pageNumber,
				limit: 10,
			});
		}
	};

	// ✅ Function to clear notifications (on clear button click)
	const clearNotifications = () => {
		if (socket.current && connected) {
			socket.current.emit(socketEvents.clearNotifications);
		}
	};

	useEffect(() => {
		if (user && token) {
			if (socket.current) {
				socket.current.off();
				socket.current.disconnect();
				socket.current = null;
			}
			initiateSocket();
		}

		return () => {
			if (socket.current) {
				socket.current.off();
				socket.current.disconnect();
				socket.current = null;
			}
		};
	}, [user, token]);

	return (
		<SocketContext.Provider
			value={{
				socket: socket.current,
				isConnected: connected,
				socketEvents,
				initiateSocket,
				notifications,
				fetchNotifications,
				clearNotifications,
			}}
		>
			{children}
		</SocketContext.Provider>
	);
};
